import type { ReactElement } from 'react';
import { Navigate, Outlet } from 'react-router-dom';

import { useAuth } from '@/providers/AuthProvider.js';

import { RouteFallback } from './RouteFallback.js';
import { ROUTES } from './routes.js';

/**
 * Guard'ы маршрутов. Они же — ЕДИНСТВЕННОЕ место, где происходит перенаправление
 * при потере сессии: клиент API её только чистит (см. create-auth-client.ts), потому
 * что лишь здесь известно, защищённая эта страница или публичная.
 *
 * Состояние `loading` обязано показывать заглушку, а не считаться гостем: иначе на
 * холодной загрузке авторизованный пользователь на долю секунды увидит редирект на вход.
 */

/**
 * ⚠️ Guard'а «только для авторизованных» здесь НЕТ, и это не упущение.
 *
 * Защищённого адреса в продукте не существует: `/` сам выбирает лендинг или кабинет
 * БЕЗ редиректа (иначе ломается атрибуция /go/:campaign), `/payment-result`
 * намеренно открыт (человек возвращается из платёжного шлюза, и его сессия за это время
 * могла истечь — показать результат важнее, чем увести на вход), `/auth/verify` обязан
 * работать для гостя. Волна 06 завела `AuthenticatedRoute` авансом, он простоял всю
 * волну без единой ссылки и удалён волной 07.
 *
 * Админке волны 08 он бы и не подошёл: ей нужен guard над ДРУГИМ пространством токенов
 * (lib/auth/admin-session-store.ts), а этот жёстко читает пользовательский useAuth().
 */

/**
 * Только для гостей. Авторизованного уводит в кабинет — это поведение PHP:
 * страница входа залогиненному не показывалась.
 */
export function GuestOnlyRoute(): ReactElement {
  const { status } = useAuth();

  if (status === 'loading') return <RouteFallback />;
  if (status === 'authenticated') return <Navigate to={ROUTES.home} replace />;
  return <Outlet />;
}
